/**
 * Shared semantic contract for AI canvas-selection context.
 *
 * The frontend builds this context after user selections and client-side
 * canvas mutations. The backend consumes it in prompts and hard mutation
 * guards. Keep this package pure and dependency-free so both sides can import
 * it without creating frontend/backend coupling.
 */

export * from './canvas-world';
export * from './resize-limits';

export const CANVAS_SELECTION_SEMANTIC_ROLES = [
  'object',
  'group',
  'frame',
  'database',
  'pdf',
  'attachment',
  'mindmap',
  'area',
  'area_label',
  'editable_target',
  'template_helper',
] as const;

export type CanvasSelectionSemanticRole =
  (typeof CANVAS_SELECTION_SEMANTIC_ROLES)[number];

export const CANVAS_SELECTION_ROLE = {
  Object: 'object',
  Group: 'group',
  Frame: 'frame',
  Database: 'database',
  Pdf: 'pdf',
  Attachment: 'attachment',
  Mindmap: 'mindmap',
  Area: 'area',
  AreaLabel: 'area_label',
  EditableTarget: 'editable_target',
  TemplateHelper: 'template_helper',
} as const satisfies Record<string, CanvasSelectionSemanticRole>;

const CANVAS_SELECTION_ROLE_SET = new Set<string>(
  CANVAS_SELECTION_SEMANTIC_ROLES
);

export function isCanvasSelectionSemanticRole(
  value: unknown
): value is CanvasSelectionSemanticRole {
  return typeof value === 'string' && CANVAS_SELECTION_ROLE_SET.has(value);
}

export interface CanvasSelectionBounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface CanvasSelectionSemanticItem {
  id: string;
  kind: string;
  type: string;
  role?: CanvasSelectionSemanticRole | null;
  label: string;
  text?: string | null;
  bounds?: CanvasSelectionBounds | null;
  parentId?: string | null;
  depth?: number | null;
  childCount?: number | null;
  groupId?: string | null;
  children?: CanvasSelectionSemanticItem[];
}

export interface CanvasTemplateAreaDefinition {
  label: string;
  aliases: readonly string[];
}

export interface CanvasTemplateSemanticSpec {
  id: string;
  labels: readonly string[];
  areas: readonly CanvasTemplateAreaDefinition[];
  editableTargetAliases?: readonly string[];
}

export function normalizeCanvasSemanticText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}
